import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import Spinner from '@components/molecules/Spinner';
import { useSetRecoilState } from 'recoil';
import {
  CallGetCategories,
  CallGetProducts,
  CallGetSimpleCategories,
  CallPutSortCategory,
  CallPutSortOptionGroup,
} from '@library/request/callProduct';
import { pageInputChange } from '@src/recoil/common';
import { useMenuPermission } from '@src/recoil/permission';
import {
  Categories,
  Product,
  PRODUCT_KEYS,
  Products,
  ProductsChild,
  ProductsOption,
  SimpleCategory,
  SortableItemProps,
  SortableListProps,
} from '@type/productType';
import ScrollTopBtn from '@src/components/molecules/ScrollTopBtn';

// 카테고리,메뉴,옵션그룹 순서 수정
const MenuSort = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const storeId = searchParams.get('storeID');
  const productID = searchParams.get('productID');
  const categoryID = searchParams.get('categoryID');

  const [sortList, setSortList] = useState<(SimpleCategory | Product | ProductsOption)[]>([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<'category' | 'product' | 'optGroup'>();

  const setIsPageInputChange = useSetRecoilState(pageInputChange);
  const { isNoAuth7 } = useMenuPermission(storeId);

  // 카테고리 리스트 조회 API
  const { data: categoryList } = useQuery([PRODUCT_KEYS.SIMPLECATEGORY, storeId], {
    enabled: !!storeId && type === 'category',
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetSimpleCategories(storeId);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // 메뉴 목록 조회 API
  const { data: productList } = useQuery([PRODUCT_KEYS.CATEGORY, storeId], {
    enabled: !!storeId && !!categoryID && type === 'product',
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetCategories(storeId);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // 옵션그룹 조회 API
  const { data: optionGroupList } = useQuery<Products>([PRODUCT_KEYS.PRODUCT, storeId, productID], {
    enabled: !!storeId && !!productID && type === 'optGroup',
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetProducts(storeId, productID);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // 카테고리/메뉴 순서 수정 API
  const { mutate: sortCategoryMutation } = useMutation(
    (param: { categories: (SimpleCategory | Categories)[] }) => CallPutSortCategory(storeId, param),
    {
      onMutate: () => setLoading(true),
      onSuccess: (response) => {
        const { result } = response;
        if (result.code !== 1) {
          alert(result.message);
        } else {
          queryClient.invalidateQueries([PRODUCT_KEYS.SIMPLECATEGORY, storeId]);
          queryClient.invalidateQueries([PRODUCT_KEYS.CATEGORY, storeId]);
          navigate(`/product/list?tab=order&storeID=${storeId}`);
        }
      },
      onError: (error) => console.log('error:', error),
      onSettled: () => setLoading(false),
    },
  );

  // 옵션그룹 순서 수정 API
  const { mutate: sortOptGroupMutation } = useMutation(
    (param: {
      optionGroups: {
        ID: number;
        options: { ID: string }[];
      }[];
    }) => CallPutSortOptionGroup(storeId, productID, param),
    {
      onMutate: () => setLoading(true),
      onSuccess: (response) => {
        const { result } = response;
        if (result.code !== 1) {
          alert(result.message);
        } else {
          queryClient.invalidateQueries([PRODUCT_KEYS.PRODUCT, storeId, productID]);
          navigate(`/product/list?tab=order&storeID=${storeId}`);
        }
      },
      onError: (error) => console.log('error:', error),
      onSettled: () => setLoading(false),
    },
  );

  const determineType = () => {
    if (productID) return 'optGroup';
    if (categoryID) return 'product';
    return 'category';
  };

  const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    if (isNoAuth7) return;
    const newSortList = [...sortList];
    const [removed] = newSortList.splice(oldIndex, 1);
    newSortList.splice(newIndex, 0, removed);
    setSortList(newSortList);
    setIsPageInputChange(true);
  };

  const onClickSave = () => {
    if (type === 'category') {
      // 카테고리 순서 수정
      sortCategoryMutation({ categories: sortList as SimpleCategory[] }); // API 호출
    } else if (type === 'product') {
      // 메뉴 순서 수정
      const updatedProductList = productList.map((category) => {
        if (category.categoryID === categoryID) {
          return {
            ...category,
            products: sortList as Product[],
          };
        }
        return category;
      });
      sortCategoryMutation({ categories: updatedProductList as Categories[] }); // API 호출
    } else {
      // 옵션그룹 순서 수정
      const send = {
        optionGroups: (sortList as ProductsOption[]).map((data) => ({
          ID: data.optionGroupID,
          options: (data.children as ProductsChild[]).map((option) => ({
            ID: option.optionID,
          })),
        })),
      };
      sortOptGroupMutation(send); // API 호출
    }
  };

  useEffect(() => {
    if (!type) return;
    switch (type) {
      case 'category':
        if (categoryList) setSortList(categoryList);
        break;
      case 'product': {
        if (productList) {
          const matchedCategory = productList.find((list) => list.categoryID === categoryID);
          setSortList(matchedCategory?.products ?? []);
        }
        break;
      }
      case 'optGroup':
        if (optionGroupList?.options) {
          const excludePriceOpt = optionGroupList.options.filter((opt) => Number(opt.isPriceOption) !== 1);
          setSortList(excludePriceOpt);
        }
        break;
      default:
        break;
    }
  }, [categoryList, productList, optionGroupList, type, categoryID]);

  useEffect(() => {
    setType(determineType());
  }, [storeId, productID, categoryID]);

  const SortableItem = SortableElement<SortableItemProps>(({ list }: SortableItemProps) => {
    const title =
      'categoryName' in list ? list.categoryName : 'name' in list ? list.name : 'title' in list ? list.title : '';
    return (
      <li className="list-sortable-item" style={{ listStyleType: 'none' }}>
        {title || ''}
      </li>
    );
  });

  const SortableList = SortableContainer<SortableListProps>(({ items }: SortableListProps) => {
    return (
      <ul id="sortbleList" className="list-sortble">
        {items.map((item, index) => {
          const ID = 'categoryID' in item ? item.categoryID : 'productID' in item ? item.productID : item.optionGroupID;
          return <SortableItem key={`category-${ID}`} index={index} list={item} />;
        })}
      </ul>
    );
  });

  return (
    <article id="mArticle" className="art-product menu-sortable-list">
      <div className="view-sort">
        {sortList.length > 0 && (
          <SortableList
            items={sortList}
            onSortEnd={onSortEnd}
            helperClass="list-sortable-item select"
            useDragHandle={false}
            useWindowAsScrollContainer
          />
        )}
      </div>
      <div className="fixedBtnGroup">
        <button type="button" id="btnSubmit" className="btnDone" onClick={onClickSave} disabled={isNoAuth7}>
          저장
        </button>
      </div>
      <ScrollTopBtn />
      <Spinner show={loading} />
    </article>
  );
};

export default MenuSort;
